import React, { useState, useEffect, useCallback } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	Paper,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, FileDownload, Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Papa from "papaparse";
import BRANDING from "../config/branding";


dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const API_URL = "http://localhost:8080/audit_logs.php";

export default function AuditLogs() {
	const [auditLogs, setAuditLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const [globalSearch, setGlobalSearch] = useState("");
	const [filterOpen, setFilterOpen] = useState(false);
	const [tempFromDate, setTempFromDate] = useState(null);
	const [tempToDate, setTempToDate] = useState(null);
	const [tempFullName, setTempFullName] = useState("");
	const [tempLogAction, setTempLogAction] = useState("");

	const [appliedFromDate, setAppliedFromDate] = useState(null);
	const [appliedToDate, setAppliedToDate] = useState(null);
	const [appliedFullName, setAppliedFullName] = useState("");
	const [appliedLogAction, setAppliedLogAction] = useState("");

	const fetchAuditLogs = useCallback(async () => {
		setLoading(true);
		setError(null);
		const token = localStorage.getItem("token");
		try {
			const response = await fetch(API_URL, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
				withCredentials: true,
			});
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const data = await response.json();
			if (!data.status) {
				toast.error(data.message);
			}
			setAuditLogs(data.data);
		} catch (err) {
			console.error("Error fetching audit logs:", err);
			setError(
				`Failed to load audit logs: ${err.message}. Please check API URL.`
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAuditLogs();
	}, [fetchAuditLogs]);

	const filteredLogs = auditLogs.filter((log) => {
		const matchesGlobalSearch = globalSearch
			? log.fullName.toLowerCase().includes(globalSearch.toLowerCase()) ||
			  log.logAction.toLowerCase().includes(globalSearch.toLowerCase())
			: true;

		const createdAt = dayjs(log.createdAt);
		const matchesDateRange =
			(!appliedFromDate ||
				createdAt.isSameOrAfter(dayjs(appliedFromDate), "day")) &&
			(!appliedToDate || createdAt.isSameOrBefore(dayjs(appliedToDate), "day"));

		const matchesFullName = appliedFullName
			? log.fullName.toLowerCase().includes(appliedFullName.toLowerCase())
			: true;

		const matchesLogAction = appliedLogAction
			? log.logAction.toLowerCase().includes(appliedLogAction.toLowerCase())
			: true;

		return (
			matchesGlobalSearch &&
			matchesDateRange &&
			matchesFullName &&
			matchesLogAction
		);
	});

	const columns = [
		{ field: "id", headerName: "ID", width: 90 },
		{ field: "fullName", headerName: "Full Name", width: 200 },
		{ field: "logAction", headerName: "Action", flex: 1, minWidth: 300 },
		{
			field: "createdAt",
			headerName: "Timestamp",
			width: 200,
			valueFormatter: (params) => {
				return params ? dayjs(params).format("YYYY-MM-DD HH:mm:ss") : "N/A";
			},
		},
	];

	const handleApplyFilter = () => {
		setAppliedFromDate(tempFromDate);
		setAppliedToDate(tempToDate);
		setAppliedFullName(tempFullName);
		setAppliedLogAction(tempLogAction);
		setFilterOpen(false);
	};

	const handleResetFilters = () => {
		setTempFromDate(null);
		setTempToDate(null);
		setTempFullName("");
		setTempLogAction("");
		setAppliedFromDate(null);
		setAppliedToDate(null);
		setAppliedFullName("");
		setAppliedLogAction("");
	};

	const exportToCSV = (data, filename = "audit_logs.csv") => {
		if (!data || !data.length) {
			toast.warn("No data available to export");
			return;
		}
		const exportData = data.map((log) => ({
			ID: log.id,
			"Full Name": log.fullName,
			Action: log.logAction,
			Timestamp: dayjs(log.createdAt).format("YYYY-MM-DD HH:mm:ss"),
		}));
		const csv = Papa.unparse(exportData);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleExport = () => {
		exportToCSV(filteredLogs);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box className='p-4 bg-white rounded-md shadow-sm'>
			<>
    <Typography
        variant='h4'
        sx={{
            fontWeight: 700,
            color: BRANDING.primaryColor
        }}
    >
        Audit Logs
    </Typography>

    <Typography
        variant='body2'
        sx={{
            color: "#64748B",
            mb: 3,
        }}
    >
        Monitor user activities and system actions across the platform
    </Typography>
</>

				<Box className='flex flex-wrap items-center justify-between gap-3 mb-6'>
					<TextField
						placeholder='Search name or action'
						variant='outlined'
						size='small'
						value={globalSearch}
						onChange={(e) => setGlobalSearch(e.target.value)}
						sx={{ minWidth: "220px", flexGrow: 1 }}
						InputProps={{
							startAdornment: <Search sx={{ mr: 1, color: BRANDING.primaryColor }} />,
							sx: { borderRadius: "8px", paddingLeft: "8px" },
						}}
					/>

					<Box className='flex items-center gap-3'>
						<Button
							variant='outlined'
							sx={{
								backgroundColor: "white",
								color: BRANDING.primaryColor,
borderColor: BRANDING.primaryColor,
"&:hover": { backgroundColor: "#FEF2F2" },
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "80px",
								padding: "8px 16px",
							}}
							onClick={() => {
								setTempFromDate(appliedFromDate);
								setTempToDate(appliedToDate);
								setTempFullName(appliedFullName);
								setTempLogAction(appliedLogAction);
								setFilterOpen(true);
							}}
						>
							Filter
						</Button>

						<Button
							variant='contained'
							startIcon={<FileDownload />}
							sx={{
								background: `linear-gradient(135deg, ${BRANDING.primaryColor}, #B91C1C)`,
								color: "white",
							"&:hover": { background: "#B91C1C" },
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "80px",
								padding: "8px 16px",
							}}
							onClick={handleExport}
						>
							Export
						</Button>
					</Box>
				</Box>

				{loading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress sx={{ color: BRANDING.primaryColor }} />
						<Typography variant='body1' sx={{ ml: 2, mt: 0.5, color: BRANDING.primaryColor}}>
							Loading audit logs...
						</Typography>
					</Box>
				)}

				{error && (
					<Alert severity='error' sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{!loading && !error && (
					<Paper
						sx={{
							height: 600,
							width: "100%",
							boxShadow: "none",
							border: "1px solid #E2E8F0",
							borderRadius: "8px",
						}}
					>
						<DataGrid
    rows={filteredLogs}
    columns={columns}
    pageSizeOptions={[10, 25, 50]}
    disableRowSelectionOnClick
    sx={{
        border: 0,

        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#FEF2F2",
            color: BRANDING.primaryColor,
            fontWeight: 700,
            borderBottom: "2px solid #E2E8F0",
        },

        "& .MuiDataGrid-row:hover": {
           backgroundColor: "#FEF2F2",
        },

        "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #F1F5F9",
        },

        "& .MuiDataGrid-footerContainer": {
           backgroundColor: "#FEF2F2",
            borderTop: "1px solid #E2E8F0",
        },

        "& .MuiIconButton-root": {
           color: BRANDING.primaryColor,
        },

        "& .MuiTablePagination-root": {
           color: BRANDING.primaryColor,
        },
    }}
/>
					</Paper>
				)}

				<Dialog
					open={filterOpen}
					onClose={() => setFilterOpen(false)}
					maxWidth='xs'
					fullWidth
					sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }}
				>
					<DialogTitle
						className='flex justify-between items-center'
						sx={{
							fontWeight: "600",
							fontSize: "1.1rem",
							pb: 1.5,
							pt: 2,
							px: 3,
							backgroundColor: "#FEF2F2",
							color: BRANDING.primaryColor,
							fontWeight: 700,
						}}
					>
						<span>Filter by</span>
						<IconButton onClick={() => setFilterOpen(false)} sx={{ p: 0.5 }}>
							<Close sx={{ color: BRANDING.primaryColor }} />
						</IconButton>
					</DialogTitle>
					<DialogContent
						dividers
						sx={{ borderBottom: "none", px: 3, pt: 2, pb: 2 }}
						className='space-y-4'
					>
						<Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
							Date
						</Typography>
						<Box className='grid grid-cols-2 gap-4'>
							<DatePicker
								label='From'
								value={tempFromDate}
								onChange={(val) => setTempFromDate(val)}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
							/>
							<DatePicker
								label='To'
								value={tempToDate}
								onChange={(val) => setTempToDate(val)}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
							/>
						</Box>
						<TextField
							label='Full Name'
							placeholder='Enter full name'
							variant='outlined'
							fullWidth
							size='small'
							value={tempFullName}
							onChange={(e) => setTempFullName(e.target.value)}
						/>
						<TextField
							label='Log Action'
							placeholder='Enter log action'
							variant='outlined'
							fullWidth
							size='small'
							value={tempLogAction}
							onChange={(e) => setTempLogAction(e.target.value)}
						/>
						<Box className='flex justify-between items-center pt-4'>
							<Button
								variant='outlined'
								sx={{
									textTransform: "none",
									borderRadius: "8px",
									borderColor: BRANDING.primaryColor,
									color: BRANDING.primaryColor,
									"&:hover": {  background: "#16257A" },
								}}
								onClick={handleResetFilters}
							>
								Reset
							</Button>
							<Button
								variant='contained'
								sx={{
									background: `linear-gradient(135deg, ${BRANDING.primaryColor}, #B91C1C)`,
									color: "white",
									"&:hover": {  background: "#16257A" },
									textTransform: "none",
									borderRadius: "8px",
								}}
								onClick={handleApplyFilter}
							>
								Apply Filter
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</Box>
		</LocalizationProvider>
	);
}
